import Konva from 'konva';
import { ExportedUtils } from './utils';
import { ExportedBoxMath } from './box-math';
import { SNAP_DIST } from './global-constants';

export class ExportedBox2D {
	public readonly verticalLineSizePxPerIn: number = 3340 / 211.0236;
	public readonly horizontalLineSizePxPerIn: number = 3091.7 / 211.0236;

	public debugEnabled: boolean = false;

	public dimensionsElements: any[] = [];
	public boxDebugElements: any[] = [];

	public exportedUtils: ExportedUtils;
	public exportedBoxMath: ExportedBoxMath;

	public constructor() {
		this.exportedUtils = new ExportedUtils();
		this.exportedBoxMath = new ExportedBoxMath();
	}

	public clearDimensions() {
		for (const e of this.dimensionsElements) {
			e.destroy();
		}

		this.dimensionsElements.length = 0;
	}

	public getDimensionsPoints(boxes) {
		const distanceFromBoxes = 50;

		let topMostBox = boxes[0];
		let bottomMostBox = boxes[0];
		let rightMostBox = boxes[0];
		let leftMostBox = boxes[0];

		for (let i = 1; i < boxes.length; i++) {
			const currentBox = boxes[i];

			if (topMostBox.attrs.y > currentBox.attrs.y) {
				topMostBox = currentBox;
			}
			if (bottomMostBox.attrs.y + bottomMostBox.attrs.height < currentBox.attrs.y + currentBox.attrs.height) {
				bottomMostBox = currentBox;
			}
			if (rightMostBox.attrs.x + rightMostBox.attrs.width < currentBox.attrs.x + currentBox.attrs.width) {
				rightMostBox = currentBox;
			}
			if (leftMostBox.attrs.x > currentBox.attrs.x) {
				leftMostBox = currentBox;
			}
		}

		const verticalLineStartX = rightMostBox.attrs.x + rightMostBox.attrs.width + distanceFromBoxes;
		const verticalLineStartY = topMostBox.attrs.y;
		const verticalLineEndX = rightMostBox.attrs.x + rightMostBox.attrs.width + distanceFromBoxes;
		const verticalLineEndY = bottomMostBox.attrs.y + bottomMostBox.attrs.height;

		const verticalLineSize = verticalLineEndY - verticalLineStartY;

		const verticalLineTextIn = verticalLineSize / this.verticalLineSizePxPerIn;
		const verticalLineTextFtIn = this.exportedUtils.inToFtAndIn(verticalLineTextIn);

		const verticalLineTextX = verticalLineStartX + distanceFromBoxes;
		const verticalLineTextY = verticalLineStartY + (verticalLineEndY - verticalLineStartY) / 2;

		const horizontalLineStartX = leftMostBox.attrs.x;
		const horizontalLineStartY = bottomMostBox.attrs.y + bottomMostBox.attrs.height + distanceFromBoxes;

		const horizontalLineEndX = rightMostBox.attrs.x + rightMostBox.attrs.width;
		const horizontalLineEndY = bottomMostBox.attrs.y + bottomMostBox.attrs.height + distanceFromBoxes;

		const horizontalLineSize = horizontalLineEndX - horizontalLineStartX;

		const horizontalLineTextIn = horizontalLineSize / this.horizontalLineSizePxPerIn;
		const horizontalLineTextFtIn = this.exportedUtils.inToFtAndIn(horizontalLineTextIn);

		const horizontalLineTextX = horizontalLineStartX + (horizontalLineEndX - horizontalLineStartX) / 2 - 75;
		const horizontalLineTextY = horizontalLineStartY + distanceFromBoxes;

		return {
			topMostBox,
			bottomMostBox,
			rightMostBox,
			leftMostBox,
			verticalLineStartX,
			verticalLineStartY,
			verticalLineEndX,
			verticalLineEndY,
			verticalLineTextX,
			verticalLineTextY,
			verticalLineTextIn,
			verticalLineTextFtIn,
			horizontalLineStartX,
			horizontalLineStartY,
			horizontalLineEndX,
			horizontalLineEndY,
			horizontalLineTextX,
			horizontalLineTextY,
			horizontalLineTextIn,
			horizontalLineTextFtIn
		};
	}

	public drawDimensions(layer, boxes): void {
		this.clearDimensions();

		if (boxes.length <= 0) {
			return;
		}

		const data = this.getDimensionsPoints(boxes);

		if (this.debugEnabled) {
			this.dimensionsElements.push(new Konva.Line({
				points: [data.topMostBox.attrs.x, data.topMostBox.attrs.y, data.verticalLineStartX, data.verticalLineStartY],
				stroke: 'black',
				strokeWidth: 6,
				lineCap: 'round',
				lineJoin: 'round',
				dash: [10, 10],
			}));

			this.dimensionsElements.push(new Konva.Line({
				points: [data.bottomMostBox.attrs.x, data.bottomMostBox.attrs.y + data.bottomMostBox.attrs.height, data.verticalLineEndX, data.verticalLineEndY],
				stroke: 'black',
				strokeWidth: 6,
				lineCap: 'round',
				lineJoin: 'round',
				dash: [10, 10],
			}));
		}

		this.dimensionsElements.push(new Konva.Line({
			points: [data.verticalLineStartX, data.verticalLineStartY, data.verticalLineEndX, data.verticalLineEndY],
			stroke: 'black',
			strokeWidth: 6,
			lineCap: 'round',
			lineJoin: 'round',
		}));

		this.dimensionsElements.push(new Konva.Circle({
			x: data.verticalLineStartX,
			y: data.verticalLineStartY,
			radius: 12,
			fill: 'black'
		}));

		this.dimensionsElements.push(new Konva.Circle({
			x: data.verticalLineEndX,
			y: data.verticalLineEndY,
			radius: 12,
			fill: 'black'
		}));

		if (!this.debugEnabled) {
			this.dimensionsElements.push(new Konva.Text({
				x: data.verticalLineTextX,
				y: data.verticalLineTextY,
				text: (data.verticalLineTextFtIn.ft
								? data.verticalLineTextFtIn.ft.toFixed(0) + ' ft '
								: '')
						+ data.verticalLineTextFtIn.in.toFixed(1) + ' in',
				fontSize: 50,
				fontFamily: 'Calibri',
				fill: 'black'
			}));
		} else {
			this.dimensionsElements.push(new Konva.Text({
				x: data.verticalLineTextX,
				y: data.verticalLineTextY,
				text: data.verticalLineTextIn.toFixed(1) + ' in',
				fontSize: 50,
				fontFamily: 'Calibri',
				fill: 'black'
			}));
		}

		if (this.debugEnabled) {
			this.dimensionsElements.push(new Konva.Line({
				points: [data.leftMostBox.attrs.x, data.leftMostBox.attrs.y, data.horizontalLineStartX, data.horizontalLineStartY],
				stroke: 'black',
				strokeWidth: 6,
				lineCap: 'round',
				lineJoin: 'round',
				dash: [10, 10],
			}));

			this.dimensionsElements.push(new Konva.Line({
				points: [data.rightMostBox.attrs.x + data.rightMostBox.attrs.width, data.rightMostBox.attrs.y, data.horizontalLineEndX, data.horizontalLineEndY],
				stroke: 'black',
				strokeWidth: 6,
				lineCap: 'round',
				lineJoin: 'round',
				dash: [10, 10],
			}));
		}

		this.dimensionsElements.push(new Konva.Line({
			points: [data.horizontalLineStartX, data.horizontalLineStartY, data.horizontalLineEndX, data.horizontalLineEndY],
			stroke: 'black',
			strokeWidth: 6,
			lineCap: 'round',
			lineJoin: 'round',
		}));

		this.dimensionsElements.push(new Konva.Circle({
			x: data.horizontalLineStartX,
			y: data.horizontalLineStartY,
			radius: 12,
			fill: 'black'
		}));

		this.dimensionsElements.push(new Konva.Circle({
			x: data.horizontalLineEndX,
			y: data.horizontalLineEndY,
			radius: 12,
			fill: 'black'
		}));

		if (this.debugEnabled) {
			this.dimensionsElements.push(new Konva.Text({
				x: data.horizontalLineTextX,
				y: data.horizontalLineTextY,
				text: data.horizontalLineTextIn.toFixed(1) + ' in',
				fontSize: 50,
				fontFamily: 'Calibri',
				fill: 'black'
			}));
		} else {
			this.dimensionsElements.push(new Konva.Text({
				x: data.horizontalLineTextX,
				y: data.horizontalLineTextY,
				text: (data.horizontalLineTextFtIn.ft
								? data.horizontalLineTextFtIn.ft.toFixed(0) + ' ft '
								: '')
						+ data.horizontalLineTextFtIn.in.toFixed(1) + ' in',
				fontSize: 50,
				fontFamily: 'Calibri',
				fill: 'black'
			}));
		}

		for (const e of this.dimensionsElements) {
			layer.add(e);
		}
	}

	public clearBoxDebugElements(): void {
		for (const g of this.boxDebugElements) {
			for (const el of g.elements) {
				el.destroy();
			}
		}

		this.boxDebugElements.length = 0;
	}

	public drawBoxDebugElements(layer, box): void {
		if (!this.debugEnabled) {
			this.clearBoxDebugElements();

			return;
		}

		let group = undefined;

		for (const g of this.boxDebugElements) {
			if (g.box === box) {
				group = g;

				break;
			}
		}

		if (group) {
			for (const el of group.elements) {
				el.destroy();
			}

			group.elements.length = 0;
		} else {
			group = {
				box: box,
				elements: []
			};

			this.boxDebugElements.push(group);
		}

		const index = box.attrs.box_index;

		group.elements.push(new Konva.Circle({
			x: box.attrs.x,
			y: box.attrs.y,
			radius: 2,
			fill: 'red'
		}));
		group.elements.push(new Konva.Circle({
			x: box.attrs.x + box.attrs.width,
			y: box.attrs.y,
			radius: 2,
			fill: 'red'
		}));
		group.elements.push(new Konva.Circle({
			x: box.attrs.x + box.attrs.width,
			y: box.attrs.y + box.attrs.height,
			radius: 2,
			fill: 'red'
		}));
		group.elements.push(new Konva.Circle({
			x: box.attrs.x,
			y: box.attrs.y + box.attrs.height,
			radius: 2,
			fill: 'red'
		}));

		group.elements.push(new Konva.Text({
					x: box.attrs.x + box.attrs.width / 2,
					y: box.attrs.y + box.attrs.height / 2 - 20,
					text: `i=${index}`,
					fontSize: 50,
					fontFamily: 'Calibri',
					fill: 'black'
				})
		);

		for (const e of group.elements) {
			layer.add(e);
		}
	}

	public getPositionAfterSnaps(box1, box2): { x: number, y: number } {
		const _ll_dist = this.exportedBoxMath.ll_dist(box1, box2);
		const _rr_dist = this.exportedBoxMath.rr_dist(box1, box2);
		const _lr_dist = this.exportedBoxMath.lr_dist(box1, box2);
		const _rl_dist = this.exportedBoxMath.rl_dist(box1, box2);
		const _tt_dist = this.exportedBoxMath.tt_dist(box1, box2);
		const _bb_dist = this.exportedBoxMath.bb_dist(box1, box2);
		const _tb_dist = this.exportedBoxMath.tb_dist(box1, box2);
		const _bt_dist = this.exportedBoxMath.bt_dist(box1, box2);

		let position = {
			x: box1.attrs.x,
			y: box1.attrs.y
		};

		if (Math.abs(_ll_dist) < SNAP_DIST) {
			position = {x: box2.attrs.x, y: position.y};
		}

		if (Math.abs(_rr_dist) < SNAP_DIST) {
			position = {x: box2.attrs.x - box1.attrs.width + box2.attrs.width, y: position.y};
		}

		if (Math.abs(_lr_dist) < SNAP_DIST) {
			position = {x: box2.attrs.x + box2.attrs.width, y: position.y};
		}

		if (Math.abs(_rl_dist) < SNAP_DIST) {
			position = {x: box2.attrs.x - box1.attrs.width, y: position.y};
		}

		if (Math.abs(_tt_dist) < SNAP_DIST) {
			position = {x: position.x, y: box2.attrs.y};
		}

		if (Math.abs(_bb_dist) < SNAP_DIST) {
			position = {x: position.x, y: box2.attrs.y - box1.attrs.height + box2.attrs.height};
		}

		if (Math.abs(_tb_dist) < SNAP_DIST) {
			position = {x: position.x, y: box2.attrs.y + box2.attrs.height};
		}

		if (Math.abs(_bt_dist) < SNAP_DIST) {
			position = {x: position.x, y: box2.attrs.y - box1.attrs.height};
		}

		return position;
	}

	public changeIsDebug(debug: boolean): void {
		this.debugEnabled = debug;
	}
}
